import { Fragment, ReactElement } from 'react';

import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconProps, IconSmallProps } from '@components/icons';
import { IconPinned } from '@components/icons/pinned';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';

import { Header } from 'components/headers';
import { PageContent } from 'components/Page';
import { useStore } from 'models/Provider';

import { OrderDepartments } from './department-order-list';
import { OrderJobs } from './job-order-list';

export type DraggingState = {
  id: number;
  order: number;
  index: number;
} | null;

export const JobOrdering = () => {
  const { t, current } = useStore();

  const { isEnterprise } = current.info;

  const description = isEnterprise
    ? t('company.settings.joborder.departments.desciption')
    : t('company.settings.joborder.desciption');

  return (
    <Fragment>
      <Header
        // @ts-ignore
        breadcrumbs={<Breadcrumb items={[{ label: t('company.settings.joborder.heading') }]} />}
      />
      <PageContent>
        <Grid>
          <GridColumnMiddle space={6}>
            <Text font="regular14" color="purple80" text={description} />
            {isEnterprise ? <OrderDepartments /> : <OrderJobs />}
          </GridColumnMiddle>
        </Grid>
      </PageContent>
    </Fragment>
  );
};

export const EmptyCard = (props: {
  text: string;
  icon: ReactElement<IconProps | IconSmallProps>;
  space?: 4 | 6;
}) => {
  const { text, icon, space = 6 } = props;
  return (
    <Card variant="gray" space={space}>
      <Stack space={4} orientation="horizontal">
        {icon}
        <Text font="regular16" color="purple80" text={text} />
      </Stack>
    </Card>
  );
};

type PinButtonProps = { tooltip: string; onClick: () => void; isAnimated?: boolean };

export const PinButton = (props: PinButtonProps) => (
  <StyledPinButton icon={<IconPinned color="blue100" />} variant="ghost-purple" {...props} />
);

const StyledPinButton = styled(IconButton)<Pick<PinButtonProps, 'isAnimated'>>(({ isAnimated }) =>
  !isAnimated
    ? {}
    : {
        '&:not(:hover)': {
          svg: {
            transform: 'rotate(45deg)',
            color: theme.color.purple50,
          },
        },
      }
);

const Grid = styled.div({
  display: 'grid',
  '@media (min-width: 375px)': {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridColumnGap: '1em',
    gridRowGap: '1.5em',
  },
  '@media (min-width: 600px)': {
    gridTemplateColumns: 'repeat(8, 1fr)',
    gridColumnGap: '1.5em',
    gridRowGap: '1.5em',
  },
  '@media (min-width: 960px)': {
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridColumnGap: '1.5em',
    gridRowGap: '1.5em',
  },
  '@media (min-width: 1280px)': {
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridColumnGap: '1.5em',
    gridRowGap: '1.5em',
  },
});

const GridColumnMiddle = styled(Stack)({
  '@media (min-width: 375px)': {
    gridColumn: '1 /  -1',
  },
  '@media (min-width: 600px)': {
    gridColumn: '2 /  -2',
  },
  '@media (min-width: 960px)': {
    gridColumn: '3 / -3',
  },
  '@media (min-width: 1280px)': {
    gridColumn: '4 / -4',
  },
});
